/**
 * ExpenseUploadDropzone Component
 * Drag-and-drop file upload for creating expenses with AI extraction
 */

import { useState, useRef, useCallback } from 'react';
import { expensesApi } from '../../lib/api';
import { readFileIntoMemory } from '@resida/shared';
import type { ExpenseDetail } from '../../types/expense.types';

interface ExpenseUploadDropzoneProps {
  propertyId: string;
  unitId?: string;
  onSuccess: (expense: ExpenseDetail) => void;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.pdf';

export function ExpenseUploadDropzone({ propertyId, unitId, onSuccess }: ExpenseUploadDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only PDF and image files (JPEG, PNG, GIF, WebP) are accepted.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', await readFileIntoMemory(file));
      formData.append('scope', unitId ? 'UNIT' : 'PROPERTY');
      formData.append('propertyId', propertyId);
      if (unitId) formData.append('unitId', unitId);
      // Provide defaults that can be overridden after AI extraction
      formData.append('amount', '0');
      formData.append('expenseDate', getTodayDateString());
      formData.append('description', file.name.replace(/\.[^/.]+$/, ''));

      const result = await expensesApi.upload(formData);
      onSuccess(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [propertyId, unitId, onSuccess]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file) handleFile(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFile]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  return (
    <div data-testid="expense-upload-dropzone">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload receipt or invoice. Drag and drop or click to browse files."
        aria-busy={isUploading}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        onKeyDown={handleKeyDown}
        className={[
          'border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200',
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-border bg-accent/50',
          isUploading ? 'cursor-default' : 'cursor-pointer',
        ].join(' ')}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleFileInputChange}
          className="hidden"
          aria-hidden="true"
          data-testid="expense-upload-input"
        />

        {isUploading ? (
          <div data-testid="expense-upload-loading" aria-live="polite">
            <div
              className="inline-block w-10 h-10 border-[3px] border-muted border-t-primary rounded-full mb-3"
              style={{ animation: 'spin 0.8s linear infinite' }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p className="m-0 text-sm text-muted-foreground">
              Uploading and extracting data...
            </p>
          </div>
        ) : (
          <>
            <div className="text-[32px] mb-3" aria-hidden="true">
              📎
            </div>
            <p className="m-0 mb-2 text-[15px] font-medium text-foreground">
              Drop a receipt or invoice here
            </p>
            <p className="m-0 mb-3 text-[13px] text-muted-foreground">
              or click to browse
            </p>
            <p className="m-0 text-xs text-muted-foreground">
              Accepted: PDF, JPEG, PNG, GIF, WebP
            </p>
          </>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mt-3 p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm flex justify-between items-start gap-2"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-destructive hover:text-destructive underline shrink-0 min-h-[44px]"
            aria-label="Dismiss error"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default ExpenseUploadDropzone;
